import { SetServiceDependencies } from '../../infra/startup/types';
import { IInternalService } from './IInternalService';
import { ServiceStatus } from './types';

/**
 * Abstract base class for internal services within the shell-commons repository.
 *
 * This class provides a standardized architecture for service implementation with:
 * - Consistent initialization patterns through the init() method
 * - Proper dependency injection via setDependencies() method
 * - Service readiness state management through isReady promise
 * - Constructor that only performs basic property setup
 *
 * Services extending this class must implement:
 * - init(): Service-specific initialization logic
 * - setDependencies(): Dependency injection setup
 *
 * Usage pattern:
 * 1. Instantiate service
 * 2. Call setDependencies() with required dependencies
 * 3. Call init() to initialize the service
 * 4. Await isReady promise to ensure service is ready for use
 */
export default abstract class InternalService implements IInternalService {
  protected _isReady: Promise<boolean>;
  protected _resolveIsReady!: (value: boolean) => void;
  protected _rejectIsReady!: (reason?: any) => void;
  protected _status: ServiceStatus;

  /**
   * Constructor performs only basic property setup.
   * Complex initialization should be done in the init() method.
   */
  constructor() {
    this._status = ServiceStatus.UNINITIALIZED;
    this._isReady = new Promise<boolean>((resolve, reject) => {
      this._resolveIsReady = resolve;
      this._rejectIsReady = reject;
    });
  }

  /**
   * Gets the promise that resolves when the service is ready for use.
   * This allows consumers to await service readiness before using it.
   */
  public get isReady(): Promise<boolean> {
    return this._isReady;
  }

  /**
   * Gets the current status of the service.
   */
  public get status(): ServiceStatus {
    return this._status;
  }

  /**
   * Should call this._markAsReady() when initialization is complete.
   */
  public async init(): Promise<void> {
    this._markAsReady();
  }

  /**
   * Should be called before init() to provide necessary dependencies.
   */
  public setDependencies(dependencies: SetServiceDependencies): void {}

  /**
   * Marks the service as ready and resolves the isReady promise.
   * Should be called by concrete implementations when initialization is complete.
   */
  protected _markAsReady(): void {
    this._status = ServiceStatus.READY;
    this._resolveIsReady(true);
  }

  /**
   * Marks the service as having an error and rejects the isReady promise.
   * Should be called by concrete implementations if initialization fails.
   */
  protected _markAsError(error?: any): void {
    this._status = ServiceStatus.ERROR;
    this._rejectIsReady(error);
  }

  /**
   * Sets the service status to initializing.
   * Should be called at the beginning of the init() method.
   */
  protected _markAsInitializing(): void {
    this._status = ServiceStatus.INITIALIZING;
  }
}
